/**
 * PasswordField — a self-contained password entry control for any create/change
 * flow (signup, forced temp-password change, future account password change).
 *
 * Renders:
 *   - the primary password field with a "generate strong password" button and a
 *     show/hide toggle,
 *   - a live red/yellow/green strength gauge + requirement checklist,
 *   - a confirmation field that enforces a match.
 *
 * It owns its own show/hide state and reports overall validity (policy met AND
 * the two fields match) via `onValidChange`, so the parent form can gate submit.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Wand2, Check, X as XIcon } from 'lucide-react';
import {
  evaluatePassword,
  generatePassword,
  PASSWORD_MIN_LENGTH,
} from '../../utils/passwordStrength';

interface PasswordFieldProps {
  idPrefix: string;
  /** Translated label. Defaults to "Password" in the active language. */
  label?: string;
  confirmLabel?: string;
  value: string;
  onChange: (v: string) => void;
  confirmValue: string;
  onConfirmChange: (v: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Fired whenever overall validity changes (policy met + fields match). */
  onValidChange?: (valid: boolean) => void;
}

const Requirement: React.FC<{ met: boolean; children: React.ReactNode }> = ({ met, children }) => (
  <li className="d-flex align-items-center gap-1" style={{ color: met ? '#28a745' : '#6c757d' }}>
    {met ? <Check size={13} /> : <XIcon size={13} />}
    <span>{children}</span>
  </li>
);

export const PasswordField: React.FC<PasswordFieldProps> = ({
  idPrefix,
  label,
  confirmLabel,
  value,
  onChange,
  confirmValue,
  onConfirmChange,
  disabled,
  autoFocus,
  onValidChange,
}) => {
  const { t } = useTranslation('auth');
  const [show, setShow] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  const evaluation = evaluatePassword(value);
  const matches = value.length > 0 && value === confirmValue;
  const valid = evaluation.meetsPolicy && matches;

  useEffect(() => {
    onValidChange?.(valid);
  }, [valid, onValidChange]);

  const handleGenerate = () => {
    const pw = generatePassword(16);
    onChange(pw);
    onConfirmChange(pw);
    setShow(true);
    setConfirmTouched(true);
  };

  const showMismatch = confirmTouched && confirmValue.length > 0 && !matches;

  return (
    <>
      <div className="mb-2">
        <label htmlFor={`${idPrefix}-password`} className="form-label small fw-semibold mb-1">
          {label ?? t('password.label')}
        </label>
        <div className="input-group">
          <input
            id={`${idPrefix}-password`}
            type={show ? 'text' : 'password'}
            className="form-control"
            autoComplete="new-password"
            autoFocus={autoFocus}
            required
            minLength={PASSWORD_MIN_LENGTH}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          <button
            type="button" className="btn btn-outline-secondary" tabIndex={-1}
            title={t('password.generate')} aria-label={t('password.generate')}
            disabled={disabled} onClick={handleGenerate}
          >
            <Wand2 size={16} />
          </button>
          <button
            type="button" className="btn btn-outline-secondary" tabIndex={-1}
            aria-label={show ? t('password.hide') : t('password.show')}
            disabled={disabled} onClick={() => setShow((s) => !s)}
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {/* Strength gauge */}
        <div className="d-flex align-items-center gap-2 mt-2" aria-hidden={value.length === 0}>
          <div className="d-flex gap-1 flex-grow-1">
            {[1, 2, 3, 4].map((seg) => (
              <span
                key={seg}
                style={{
                  height: 5, flex: 1, borderRadius: 3,
                  background: seg <= evaluation.level ? evaluation.color : '#e5e7eb',
                  transition: 'background 150ms ease',
                }}
              />
            ))}
          </div>
          {/* minWidth 48 was sized for "Strong"; French "Excellent" is longer, so
              the gauge label gets room to grow rather than a fixed slot. */}
          <span className="small fw-semibold" style={{ color: evaluation.color, minWidth: 48, textAlign: 'right', whiteSpace: 'nowrap' }}>
            {evaluation.labelKey ? t(evaluation.labelKey) : ''}
          </span>
        </div>

        {/* Requirement checklist */}
        <ul className="list-unstyled small mt-2 mb-0" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
          <Requirement met={evaluation.checks.length}>{t('password.reqLength', { count: PASSWORD_MIN_LENGTH })}</Requirement>
          <Requirement met={evaluation.checks.upper}>{t('password.reqUpper')}</Requirement>
          <Requirement met={evaluation.checks.lower}>{t('password.reqLower')}</Requirement>
          <Requirement met={evaluation.checks.digit}>{t('password.reqDigit')}</Requirement>
        </ul>
      </div>

      <div className="mb-3">
        <label htmlFor={`${idPrefix}-confirm`} className="form-label small fw-semibold mb-1">
          {confirmLabel ?? t('password.confirmLabel')}
        </label>
        <input
          id={`${idPrefix}-confirm`}
          type={show ? 'text' : 'password'}
          className={`form-control ${showMismatch ? 'is-invalid' : matches && confirmValue ? 'is-valid' : ''}`}
          autoComplete="new-password"
          required
          value={confirmValue}
          disabled={disabled}
          onChange={(e) => onConfirmChange(e.target.value)}
          onBlur={() => setConfirmTouched(true)}
        />
        {showMismatch && <div className="invalid-feedback d-block">{t('password.mismatch')}</div>}
      </div>
    </>
  );
};
